import PageHeader from "@/components/PageHeader";
import {
  PackageSearch,
  Eye,
  ActivitySquare,
  Tags,
  ShoppingBag,
} from "lucide-react";

const steps = [
  {
    n: "01",
    icon: PackageSearch,
    title: "Inventory Acquisition",
    desc: "We source inventory through customer returns, liquidation lots, overstock purchases, and surplus electronics from a network of established suppliers.",
    bullets: ["Customer returns", "Liquidation lots", "Overstock & surplus"],
  },
  {
    n: "02",
    icon: Eye,
    title: "Inspection",
    desc: "Every item undergoes a visual inspection. We document condition, missing components, and any cosmetic notes so the listing accurately reflects what you’ll receive.",
    bullets: ["Visual condition check", "Accessory verification", "Cosmetic notes recorded"],
  },
  {
    n: "03",
    icon: ActivitySquare,
    title: "Testing",
    desc: "Applicable products are tested for functionality. Items that do not meet our basic functionality criteria are not listed for resale.",
    bullets: ["Power-on checks", "Functional testing", "Component verification"],
  },
  {
    n: "04",
    icon: Tags,
    title: "Condition Grading",
    desc: "Products are categorized using a clear, consistent grading system so customers can make informed decisions.",
    bullets: ["New", "Open Box", "Refurbished", "Used"],
  },
  {
    n: "05",
    icon: ShoppingBag,
    title: "Resale",
    desc: "Products are offered with transparent descriptions and customer support. Listings include the grade, condition notes, and any relevant details.",
    bullets: ["Transparent listings", "Condition disclosure", "Customer support"],
  },
];

export default function Process() {
  return (
    <div data-testid="process-page">
      <PageHeader
        eyebrow="Our Process"
        breadcrumb="Our Process"
        title="From sourcing to resale — a transparent five-step process."
        subtitle="Every product that reaches our customers moves through the same consistent workflow, ensuring fair grading and accurate listings."
      />

      <section className="bg-white">
        <div className="cnl-container py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-28">
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2563EB]">
                  Workflow
                </span>
                <h2 className="mt-3 text-2xl font-bold tracking-tight text-[#0B192C]">
                  Five steps. Zero shortcuts.
                </h2>
                <p className="mt-3 text-[14.5px] text-gray-600 leading-relaxed">
                  We follow the same process for every product — from a single
                  accessory to a full hardware unit.
                </p>
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-5">
              {steps.map((step) => (
                <article
                  key={step.n}
                  className="group border border-gray-200 rounded-sm p-7 md:p-9 hover:border-[#0B192C] transition-colors bg-white"
                  data-testid={`process-step-${step.n}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="flex items-center gap-4 md:flex-col md:items-start md:gap-3">
                      <span className="mono text-[28px] font-semibold text-[#1E3E62] leading-none">
                        {step.n}
                      </span>
                      <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0B192C] text-white">
                        <step.icon size={20} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-[22px] font-bold tracking-tight text-[#0B192C]">
                        {step.title}
                      </h3>
                      <p className="mt-3 text-[15px] leading-relaxed text-gray-700">
                        {step.desc}
                      </p>
                      <ul className="mt-5 flex flex-wrap gap-2">
                        {step.bullets.map((b) => (
                          <li
                            key={b}
                            className="rounded-sm border border-gray-200 bg-gray-50 px-3 py-1.5 text-[12px] font-medium text-gray-700"
                          >
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
